import React, { FormEvent, useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router";
import { ButtonsContainer, ErrorMessage } from "../UIComponents";
import { FormWrapperWide, ShadowedBox, Title } from "../UIComponents/styled";
import { HeaderImage } from "../UIComponents/HeaderImage";
import { AuthorizationCodeInfo } from "../AuthorizationCodeInfo/AuthorizationCodeInfo";
import { t } from "../../intl";
import Phone from "../../assets/phone.svg";
import { useValidation, ValidationSchema } from "../../utils/useValidation";
import { useAppDispatch, useAppSelector } from "../../store";
import { CommonAsyncThunkAction } from "../../pages/PasswordForm/modes";
import { getObjectKeys } from "../../utils/object";
import { loadingStatuses } from "../../constants/loadingStatuses";
import { Validator } from "../UIComponents/Validator";

export type BoxComponentProps<TFieldsValue, TFieldId = keyof TFieldsValue> = {
  fieldsValue: TFieldsValue;
  onFieldValueChange: (fieldId: TFieldId, relatedFieldId?: TFieldId) => (value: string) => void;
  onFieldValueBlur: (fieldId: TFieldId) => (value: string) => void;
  hasValidationError: (fieldId: TFieldId) => boolean;
  getValidationMessagesTrKeys: (fieldId: TFieldId) => { trKey?: string }[] | undefined;
};

interface FormLayoutProps<TFieldsValue, TFieldId = keyof TFieldsValue> {
  titleTrKey: string;
  submitTrKey: string;
  initialState: TFieldsValue;
  onSubmitAction: (fieldsValue: TFieldsValue) => CommonAsyncThunkAction;
  leftTitleTrKey: string;
  leftComponent: (props: BoxComponentProps<TFieldsValue>) => React.ReactNode;
  rightTitleTrKey: string;
  rightComponent: (props: BoxComponentProps<TFieldsValue>) => React.ReactNode;
  validationSchema: ValidationSchema<TFieldsValue>;
  validator: {
    titleTrKey: string;
    phraseFieldId: TFieldId;
    conditions: { labelTrKey: string; testFn: (phrase: string) => boolean }[];
  };
}

export const FormLayout = <TFieldsValue extends Record<string, string>>(
  props: FormLayoutProps<TFieldsValue>
) => {
  type TFieldId = keyof TFieldsValue;

  const {
    titleTrKey,
    submitTrKey,
    initialState,
    onSubmitAction,
    leftTitleTrKey,
    leftComponent,
    rightTitleTrKey,
    rightComponent,
    validationSchema,
    validator,
  } = props;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { loadingStatus, errorTrKey } = useAppSelector((state) => state.user);
  const [fieldsValue, setFieldsValue] = useState<TFieldsValue>(initialState);
  const blurredInit = Object.keys(initialState).reduce<Record<TFieldId, boolean>>((acc, curr) => {
    return { ...acc, [curr]: false };
  }, {} as Record<TFieldId, boolean>);
  const [isBlurred, setIsBlurred] = useState<Record<TFieldId, boolean>>(blurredInit);
  const [isPhraseSufficient, setIsPhraseSufficient] = useState(false);
  const { errorMessages, validateField } = useValidation(validationSchema);
  const onFieldValueChange = (fieldId: TFieldId, relatedFieldId?: TFieldId) => (value: string) => {
    setFieldsValue((prev) => ({ ...prev, [fieldId]: value }));

    const updatedFieldsValue = { ...fieldsValue, [fieldId]: value };

    validationSchema[fieldId]
      .filter(({ triggerEvent }) => triggerEvent === "change")
      .forEach(() => validateField(fieldId, value, updatedFieldsValue));

    if (relatedFieldId && isBlurred?.[relatedFieldId]) {
      validationSchema[relatedFieldId].forEach(() =>
        validateField(relatedFieldId, fieldsValue[relatedFieldId], updatedFieldsValue)
      );
    }
  };
  const onFieldValueBlur = (fieldId: TFieldId) => (value: string) => {
    const updatedFieldsValue = { ...fieldsValue, [fieldId]: value };

    validationSchema[fieldId]
      .filter(({ triggerEvent }) => triggerEvent === "blur")
      .forEach(() => validateField(fieldId, value, updatedFieldsValue));
    setIsBlurred((prev) => ({ ...prev, [fieldId]: true }));
  };
  const getValidationMessagesTrKeys = (fieldId: TFieldId) => {
    return errorMessages?.[fieldId] ? [{ trKey: errorMessages[fieldId] }] : undefined;
  };
  const hasValidationError = (fieldId: TFieldId) => {
    return !!errorMessages?.[fieldId];
  };
  const runAllValidations = () => {
    let isAllValidationsPass = true;

    getObjectKeys(validationSchema).forEach((fieldId) => {
      if (validateField(fieldId, fieldsValue[fieldId], fieldsValue)) return;
      isAllValidationsPass = false;
    });

    return isAllValidationsPass;
  };
  const onFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const isAllValidationsPass = runAllValidations();

    if (!isAllValidationsPass || loadingStatus === loadingStatuses.pending || !isPhraseSufficient) {
      return;
    }

    dispatch(onSubmitAction(fieldsValue));
  };

  useEffect(() => {
    if (!errorTrKey) return;
    runAllValidations();
  }, [errorTrKey]);

  return (
    <FormWrapperWide onSubmit={onFormSubmit}>
      <Wrapper>
        <HeaderWrapper>
          <HeaderImage alt="phone" image={Phone} />
          <Title>{t(titleTrKey)}</Title>
          <AuthorizationCodeInfo textCenter />
        </HeaderWrapper>
        <ContainerBox width={823} padding={32}>
          <ItemBox>
            <ItemTitle>{t(leftTitleTrKey)}</ItemTitle>
            {leftComponent({
              fieldsValue,
              onFieldValueChange,
              onFieldValueBlur,
              getValidationMessagesTrKeys,
              hasValidationError,
            })}
          </ItemBox>
          <ItemBox>
            <ItemTitle>{t(rightTitleTrKey)}</ItemTitle>
            <InputFormElementsWrapper>
              {rightComponent({
                fieldsValue,
                onFieldValueChange,
                onFieldValueBlur,
                getValidationMessagesTrKeys,
                hasValidationError,
              })}
            </InputFormElementsWrapper>
            <Validator
              title={t(validator.titleTrKey)}
              conditions={validator.conditions}
              phrase={fieldsValue[validator.phraseFieldId]}
              setIsPhraseSufficient={setIsPhraseSufficient}
            />
          </ItemBox>
        </ContainerBox>
        <ButtonsContainer
          justify="center"
          flatButton={{
            textTrKey: "button.back",
            onClick: () => navigate(-1),
          }}
          submitButton={{
            textTrKey: submitTrKey,
          }}
        />
      </Wrapper>
    </FormWrapperWide>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const InputFormElementsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ItemTitle = styled.p`
  font-weight: 600;
  font-size: 20px;
  line-height: 28px;
  margin: 0 0 24px;
`;

const ContainerBox = styled(ShadowedBox)`
  height: auto;
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  ${({ theme }) => theme.media.belowMd} {
    flex-direction: column;
    width: 100%;
    background-color: ${({ theme }) => theme.colors.white};
  }
`;

const ItemBox = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  ${({ theme }) => theme.media.belowMd} {
    margin-bottom: 32px;

    &:first-child {
      position: relative;

      &:before {
        content: "";
        position: absolute;
        width: 100%;
        bottom: -19px;
        height: 1px;
        background-color: ${({ theme }) => theme.colors.gray30};
      }
    }
  }

  ${({ theme }) => theme.media.aboveMd} {
    &:first-child {
      padding-right: 64px;
      position: relative;

      &:after {
        content: "";
        position: absolute;
        height: 100%;
        width: 1px;
        right: 32px;
        background-color: ${({ theme }) => theme.colors.gray30};
      }

      &:before {
        content: "";
        height: 12px;
        width: 12px;
        border-top: 1px solid ${({ theme }) => theme.colors.gray30};
        border-right: 1px solid ${({ theme }) => theme.colors.gray30};
        transform: rotate(45deg);
        position: absolute;
        right: 26px;
        top: 50%;
        z-index: 2;
        background-color: ${({ theme }) => theme.colors.white};
      }
    }
  }
`;
